import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle } from "lucide-react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@cieda/djayma-ui";

type ConflictChoice = "apply" | "keep" | "cancel";

interface ConflictResolutionDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly originalStock: number;
  readonly serverStock: number;
  readonly yourDelta: number;
  readonly onResolve: (choice: ConflictChoice) => void;
  readonly modifiedBy?: string;
  readonly modifiedAgo?: string;
}

/**
 * 3-way merge UI shown when the server returns 409 for a stock adjustment.
 * Options:
 *   - apply : re-apply user's delta on top of the new server stock
 *   - keep  : accept server stock, discard the user change
 *   - cancel: do nothing, return to the form unchanged
 */
export function ConflictResolutionDialog({
  open,
  onOpenChange,
  originalStock,
  serverStock,
  yourDelta,
  onResolve,
  modifiedBy,
  modifiedAgo,
}: ConflictResolutionDialogProps) {
  const { t } = useTranslation("admin");
  const [choice, setChoice] = useState<ConflictChoice>("apply");

  const resultIfApply = serverStock + yourDelta;

  const options: ReadonlyArray<{
    readonly value: ConflictChoice;
    readonly label: string;
  }> = [
    {
      value: "apply",
      label: t("products.conflict.option_apply", {
        delta: `${yourDelta > 0 ? "+" : ""}${yourDelta}`,
        result: resultIfApply.toLocaleString("fr-SN"),
      }),
    },
    {
      value: "keep",
      label: t("products.conflict.option_keep", {
        current: serverStock.toLocaleString("fr-SN"),
      }),
    },
    {
      value: "cancel",
      label: t("products.conflict.option_cancel"),
    },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-700">
            <AlertTriangle className="h-5 w-5" aria-hidden="true" />
            {t("products.conflict.title")}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {modifiedBy && (
            <p className="text-sm text-stone-600">
              {t("products.conflict.message", {
                actor: modifiedBy,
                time: modifiedAgo ?? "quelques secondes",
              })}
            </p>
          )}

          <div className="space-y-1 rounded-md bg-stone-50 p-3 text-sm tabular-nums">
            <div className="flex justify-between">
              <span className="text-stone-500">
                {t("products.conflict.your_stock")}
              </span>
              <span>{originalStock.toLocaleString("fr-SN")}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-stone-500">
                {t("products.conflict.current_stock")}
              </span>
              <span className="font-medium">
                {serverStock.toLocaleString("fr-SN")}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-stone-500">
                {t("products.conflict.your_change")}
              </span>
              <span>
                {yourDelta > 0 ? "+" : ""}
                {yourDelta.toLocaleString("fr-SN")}
              </span>
            </div>
          </div>

          <fieldset className="space-y-2">
            <legend className="sr-only">
              {t("products.conflict.title")}
            </legend>
            {options.map((option) => {
              const isActive = choice === option.value;
              return (
                <label
                  key={option.value}
                  className={`flex cursor-pointer items-start gap-2 rounded-md border p-3 text-sm transition-colors ${
                    isActive
                      ? "border-amber-600 bg-amber-50"
                      : "border-stone-200 hover:bg-stone-50"
                  }`}
                >
                  <input
                    type="radio"
                    name="conflict"
                    value={option.value}
                    checked={isActive}
                    onChange={() => setChoice(option.value)}
                    className="mt-0.5"
                  />
                  <span>{option.label}</span>
                </label>
              );
            })}
          </fieldset>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            type="button"
          >
            {t("products.conflict.option_cancel")}
          </Button>
          <Button onClick={() => onResolve(choice)} type="button">
            {t("products.conflict.apply_choice")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
